import { useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "reactstrap";
import { ColumnDef } from "@tanstack/react-table";
import {
  SOURCE_TYPE_URL_MAP,
  type AnySourceType,
} from "constants/source-type.constants";
import Ellipsis from "./Ellipsis";

const IndexColumn = <T,>(): ColumnDef<T> => ({
  id: "index",
  header: "#",
  enableSorting: false,
  enableColumnFilter: false,
  cell: ({ row, table }) => {
    const meta = table.options.meta as {
      pageIndex: number;
      pageSize: number;
    };

    return (
      <span className="fw-semibold">
        {String(meta.pageIndex * meta.pageSize + row.index + 1).padStart(
          2,
          "0",
        )}
      </span>
    );
  },
});

const CheckBoxColumn = <T extends { id: number | string }>(): ColumnDef<T> => ({
  id: "select",
  header: ({ table }) => (
    <input
      type="checkbox"
      className="form-check-input"
      checked={table.getIsAllRowsSelected()}
      onChange={table.getToggleAllRowsSelectedHandler()}
    />
  ),

  cell: ({ row }) => (
    <input
      type="checkbox"
      className="form-check-input"
      checked={row.getIsSelected()}
      disabled={!row.getCanSelect()}
      onChange={row.getToggleSelectedHandler()}
    />
  ),

  enableSorting: false,
  enableColumnFilter: false,
});

export type SourceColumnCell = {
  typeLabel: string;
  primaryText: string;
  href: string | null;
};

function normalizeSourceId(
  sourceId: number | string | null | undefined,
): number | null {
  if (sourceId == null || sourceId === "") return null;
  const n = typeof sourceId === "number" ? sourceId : Number(sourceId);
  return Number.isFinite(n) ? n : null;
}

export function resolveMappedSourceCell(
  sourceType: string | null | undefined,
  sourceId: number | string | null | undefined,
  options: {
    typeLabelMap: Record<string, string>;
    source?: { code?: string | null } | null;
    linkRequiresCode?: boolean;
  },
): SourceColumnCell | null {
  const { typeLabelMap, source, linkRequiresCode = false } = options;
  const id = normalizeSourceId(sourceId);
  const code = source?.code?.trim();

  if (!sourceType && id == null && !code) {
    return null;
  }

  const typeLabel =
    (sourceType && typeLabelMap[sourceType]) ?? sourceType ?? "—";

  const primaryText =
    linkRequiresCode && !code ? "—" : code || (id != null ? `#${id}` : "—");

  const base =
    sourceType != null
      ? (SOURCE_TYPE_URL_MAP[sourceType as AnySourceType] ?? null)
      : null;

  let href: string | null = null;
  if (base != null && id != null) {
    if (linkRequiresCode) {
      if (code) href = `${base}/${id}`;
    } else {
      href = `${base}/${id}`;
    }
  }

  return { typeLabel, primaryText, href };
}

export function SourceColumnContent({
  value,
}: {
  value: SourceColumnCell | null | undefined;
}) {
  if (!value) {
    return <span className="text-muted">—</span>;
  }
  const r = value;

  const codeLine =
    r.href != null ? (
      <Link to={r.href} className="link text-decoration-none">
        <CodeCell code={r.primaryText} />
      </Link>
    ) : (
      <CodeCell code={r.primaryText} />
    );

  const typeLine =
    r.typeLabel && r.typeLabel !== "—" ? (
      <Badge color="light" className="text-muted">
        {r.typeLabel}
      </Badge>
    ) : null;

  return (
    <div className="min-w-0 d-flex flex-column align-items-start gap-1 lh-sm">
      {typeLine}
      {codeLine}
    </div>
  );
}

type SourceColumnOptions<T> = {
  id?: string;
  header?: string;
  enableSorting?: boolean;
  resolve: (row: T) => SourceColumnCell | null | undefined;
  accessorFn?: (row: T) => unknown;
};

function SourceColumn<T>({
  id = "source",
  header = "Source",
  enableSorting = false,
  resolve,
  accessorFn,
}: SourceColumnOptions<T>): ColumnDef<T> {
  return {
    id,
    header,
    enableColumnFilter: false,
    enableSorting,
    ...(accessorFn != null ? { accessorFn } : {}),
    cell: ({ row }) => <SourceColumnContent value={resolve(row.original)} />,
  };
}

export function CodeCell({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    void navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <span className="d-inline-flex align-items-center gap-1">
      <span className="font-monospace">{code}</span>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-muted lh-1"
        onClick={handleCopy}
        title={copied ? "Copied!" : "Copy"}
        style={{ fontSize: "0.8rem" }}
      >
        <i
          className={
            copied ? "ri-check-line text-success" : "ri-file-copy-line"
          }
        />
      </button>
    </span>
  );
}

type CodeTitleColumnsOptions<T> = {
  titleHeader: string;
  accessorKey: string;
  resolve: (row: T) => {
    code: string;
    title?: string | null;
    to?: string | null;
  };
  enableSorting?: boolean;
};

export function CodeTitleColumns<T>({
  accessorKey,
  resolve,
  enableSorting = false,
}: CodeTitleColumnsOptions<T>): [ColumnDef<T>, ColumnDef<T>] {
  const titleCol: ColumnDef<T> = {
    id: `${accessorKey}_title`,
    header: "Title",
    enableColumnFilter: false,
    enableSorting,
    cell: ({ row }) => {
      const { title } = resolve(row.original);
      const text = title?.trim();
      if (!text) return <span className="text-muted">—</span>;

      return <Ellipsis>{text}</Ellipsis>;
    },
  };

  const codeCol: ColumnDef<T> = {
    id: accessorKey,
    header: "Code",
    accessorKey,
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ row }) => {
      const { code } = resolve(row.original);
      return <CodeCell code={code} />;
    },
  };

  return [codeCol, titleCol];
}

export { IndexColumn, CheckBoxColumn, SourceColumn };
